import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Handshake,
  IdCard,
  ReceiptText,
  Repeat,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Section } from "../components/ui/section";
import { PageHeader } from "../components/ui/page-header";
import { Avatar } from "../components/ui/avatar";
import { MoneyAmount } from "../components/MoneyAmount";
import { ExpenseAmount } from "../components/ExpenseAmount";
import { TransferParticipantDialog } from "../components/TransferParticipantDialog";
import {
  useGroup,
  useInfiniteExpenses,
  useInfiniteSettlements,
  useMe,
} from "../api/hooks";
import { canManageGroup, formatRole, getMyGroupRole } from "../lib/groupRole";
import type { Expense, Group, Participant, Settlement } from "../api/types";

/**
 * Everything one participant is involved in, inside one group (issue #448):
 * the expenses they paid or share, and the payments they sent or received.
 *
 * This is also where an OWNER can move that history onto someone else — the
 * "added as a guest, later joined with their own account" cleanup. Doing it from
 * the participant's own page means the OWNER can see exactly what will move
 * before they move it.
 */
export function ParticipantDetailPage(): ReactNode {
  const { groupId, participantId } = useParams<{
    groupId: string;
    participantId: string;
  }>();
  const navigate = useNavigate();
  const groupQ = useGroup(groupId);
  const meQ = useMe();
  const expensesQ = useInfiniteExpenses(groupId);
  const settlementsQ = useInfiniteSettlements(groupId);
  const [transferring, setTransferring] = useState(false);

  const group = groupQ.data;
  const participant = group?.participants.find((p) => p.id === participantId);
  const isOwner = canManageGroup(
    group ? getMyGroupRole(group, meQ.data) : null,
  );

  const expenses = useMemo(
    () => expensesQ.data?.pages.flatMap((page) => page.items) ?? [],
    [expensesQ.data],
  );
  const settlements = useMemo(
    () => settlementsQ.data?.pages.flatMap((page) => page.items) ?? [],
    [settlementsQ.data],
  );

  const theirExpenses = useMemo(
    () =>
      participantId === undefined
        ? []
        : expenses.filter((e) => expenseInvolves(e, participantId)),
    [expenses, participantId],
  );
  const theirSettlements = useMemo(
    () =>
      participantId === undefined
        ? []
        : settlements.filter(
            (s) => s.payerId === participantId || s.payeeId === participantId,
          ),
    [settlements, participantId],
  );

  if (groupQ.isLoading) {
    return (
      <div className="mx-auto max-w-xl space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!group || !participant) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          That participant isn&apos;t in this group.
        </CardContent>
      </Card>
    );
  }

  // Anyone else who could take this participant's history on.
  const transferTargets = group.participants.filter(
    (p) => p.id !== participant.id,
  );
  const hasMore = expensesQ.hasNextPage || settlementsQ.hasNextPage;

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader
        back={{
          label: group.label,
          onClick: () => navigate(`/groups/${group.id}?section=people`),
        }}
      />

      <div className="flex items-center gap-4">
        <Avatar name={participant.displayName} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-headline-lg">
            {participant.displayName}
          </h1>
          <p className="text-body-md text-on-surface-variant">
            {formatRole(participant.role)}
            {participant.type === "GUEST" ? " · Guest" : ""}
          </p>
        </div>
      </div>

      {/* A guest has no account to open; a USER row does (issue #452). */}
      {participant.type === "USER" && participant.userId !== undefined ? (
        <Link
          to={`/users/${participant.userId}`}
          className="inline-flex items-center gap-2 text-label-md text-primary hover:underline"
        >
          <IdCard className="size-4" />
          View account profile
        </Link>
      ) : null}

      <Section
        title="Expenses"
        description="Expenses they paid for or have a share in."
        contentClassName="space-y-stack-sm"
      >
        {expensesQ.isLoading ? (
          <Skeleton className="h-24 rounded-md" />
        ) : theirExpenses.length === 0 ? (
          <EmptyRow
            icon={<ReceiptText className="size-6" />}
            message="No expenses involve them yet."
          />
        ) : (
          theirExpenses.map((expense) => (
            <ExpenseRow
              key={expense.id}
              group={group}
              expense={expense}
              participantId={participant.id}
            />
          ))
        )}
      </Section>

      <Section
        title="Payments"
        description="Settlements they sent or received."
        contentClassName="space-y-stack-sm"
      >
        {settlementsQ.isLoading ? (
          <Skeleton className="h-24 rounded-md" />
        ) : theirSettlements.length === 0 ? (
          <EmptyRow
            icon={<Handshake className="size-6" />}
            message="No payments involve them yet."
          />
        ) : (
          theirSettlements.map((settlement) => (
            <SettlementRow
              key={settlement.id}
              group={group}
              settlement={settlement}
            />
          ))
        )}
      </Section>

      {/*
        The lists above filter whatever pages are loaded, so an older
        transaction can still be waiting on the next page. Say so rather than
        letting the page imply it has shown everything.
      */}
      {hasMore ? (
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => {
              if (expensesQ.hasNextPage) void expensesQ.fetchNextPage();
              if (settlementsQ.hasNextPage) void settlementsQ.fetchNextPage();
            }}
            disabled={
              expensesQ.isFetchingNextPage || settlementsQ.isFetchingNextPage
            }
          >
            {expensesQ.isFetchingNextPage || settlementsQ.isFetchingNextPage
              ? "Loading…"
              : "Load older history"}
          </Button>
        </div>
      ) : null}

      {isOwner ? (
        <Section
          title="Same person as someone else?"
          description="Move everything above onto another participant and delete this one. Use this when someone was added as a guest and later joined with their own account."
        >
          <Button
            variant="secondary"
            className="gap-2"
            onClick={() => setTransferring(true)}
            disabled={transferTargets.length === 0}
          >
            <Repeat className="size-4" />
            Move transactions
          </Button>
          {transferTargets.length === 0 ? (
            <p className="pt-3 text-label-sm text-on-surface-variant">
              There&apos;s nobody else in this group to move them to.
            </p>
          ) : null}
        </Section>
      ) : null}

      <TransferParticipantDialog
        group={group}
        source={participant}
        targets={transferTargets}
        open={transferring}
        onOpenChange={setTransferring}
        onTransferred={() => navigate(`/groups/${group.id}?section=people`)}
      />
    </div>
  );
}

/** True when the participant paid for the expense or has a share in any item. */
function expenseInvolves(expense: Expense, participantId: string): boolean {
  if (expense.payments.some((p) => p.participantId === participantId)) {
    return true;
  }
  return expense.items.some((item) =>
    item.split.divisions.some((d) => d.participantId === participantId),
  );
}

function ExpenseRow({
  group,
  expense,
  participantId,
}: {
  group: Group;
  expense: Expense;
  participantId: string;
}): ReactNode {
  const total = expense.items.reduce((sum, item) => sum + item.cost, 0);
  const paid = expense.payments
    .filter((p) => p.participantId === participantId)
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <Link
      to={`/groups/${group.id}/expenses/${expense.id}`}
      className="flex items-center justify-between gap-4 rounded-md border border-outline-variant/60 p-4 transition-colors hover:border-primary/50 hover:bg-surface-container/40"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-primary-fixed text-primary">
          <ReceiptText className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="break-words text-body-lg">{expense.label}</p>
          <p className="text-label-md text-on-surface-variant">
            {new Date(`${expense.expenseDate}T12:00:00`).toLocaleDateString()} ·{" "}
            {paid > 0 ? "They paid" : "Their share"}
          </p>
        </div>
      </div>
      <ExpenseAmount
        minor={total}
        currency={expense.currency}
        groupCharge={expense.groupCharge}
        groupCurrency={group.currency}
        className="shrink-0 text-headline-md"
      />
    </Link>
  );
}

function SettlementRow({
  group,
  settlement,
}: {
  group: Group;
  settlement: Settlement;
}): ReactNode {
  const nameOf = (id: string): string =>
    group.participants.find((p) => p.id === id)?.displayName ?? "Someone";

  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-outline-variant/60 p-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-tertiary-container text-on-tertiary-container">
          <Handshake className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-1.5 truncate text-body-lg">
            <span className="truncate">{nameOf(settlement.payerId)}</span>
            <ArrowRight
              className="size-3.5 shrink-0 text-on-surface-variant"
              aria-label="paid"
            />
            <span className="truncate">{nameOf(settlement.payeeId)}</span>
          </p>
          <p className="text-label-md text-on-surface-variant">
            {new Date(settlement.createdAt).toLocaleDateString()}
            {settlement.note ? ` · ${settlement.note}` : ""}
          </p>
        </div>
      </div>
      <MoneyAmount
        minor={settlement.amount}
        currency={settlement.currency}
        className="shrink-0 text-headline-md"
      />
    </div>
  );
}

function EmptyRow({
  icon,
  message,
}: {
  icon: ReactNode;
  message: string;
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
        {icon}
      </span>
      <p className="text-body-md text-on-surface-variant">{message}</p>
    </div>
  );
}

export type { Participant };
